<template>
	<el-dialog :title="$t('serviceMaintenance.batchMaintenance')" :visible.sync="dialogVisible" width="60%" :close-on-click-modal="false" @close="handleClose">
		<!-- 步骤条 -->
		<el-steps :active="stepsActiveIndex" align-center finish-status="success" style="margin-bottom: 20px">
			<el-step :title="$t('serviceMaintenance.selectUsers')"></el-step>
			<el-step :title="$t('serviceMaintenance.selectServiceProvider')"></el-step>
			<el-step :title="$t('serviceMaintenance.complete')"></el-step>
		</el-steps>

		<!-- 步骤内容 -->
		<div class="step-content">
			<!-- 第一步 -->
			<div v-show="currentStep === 0">
				<el-table
					ref="customerTable"
					:data="customerList"
					style="width: 100%"
					border
					max-height="350px"
					@selection-change="handleSelectionChange"
					:span-method="customerSpanMethod"
				>
					<el-table-column type="selection" width="55"> </el-table-column>
					<el-table-column prop="customerName" :label="$t('serviceMaintenance.customerName')" min-width="120" align="center">
						<template slot-scope="scope">
							{{ scope.row.original_phone_group.name }}
						</template>
					</el-table-column>

					<el-table-column prop="phone_number" :label="$t('serviceMaintenance.customerPhone')" width="140" align="center">
						<template slot-scope="scope">
							<span class="phone-text">{{ scope.row.phone_number }}</span>
						</template>
					</el-table-column>

					<el-table-column prop="customer_id" :label="$t('serviceMaintenance.associatedRestaurantId')" min-width="150" align="center">
						<template slot-scope="scope">
							{{ scope.row.customer_id }}
						</template>
					</el-table-column>

					<el-table-column prop="restaurantName" :label="$t('serviceMaintenance.restaurantName')" min-width="160" align="center">
						<template slot-scope="scope">
							{{ scope.row.restaurant_name }}
						</template>
					</el-table-column>
				</el-table>
			</div>

			<!-- 第二步 -->
			<div v-show="currentStep === 1">
				<el-table
					ref="serviceMaintenanceDataTable"
					:data="serviceMaintenanceData"
					style="width: 100%"
					border
					max-height="350px"
					@selection-change="handleSelectionServiceChange"
				>
					<el-table-column type="selection" width="55"> </el-table-column>
					<el-table-column prop="app_name" :label="$t('serviceMaintenance.applicationName')"></el-table-column>
					<el-table-column prop="merchant_name" :label="$t('serviceMaintenance.businessName')"></el-table-column>
					<el-table-column prop="type" :label="$t('serviceMaintenance.type')">
						<template slot-scope="scope">
							{{ scope.row.type === 1 ? $t("serviceMaintenance.service") : $t("serviceMaintenance.soft") }}
						</template>
					</el-table-column>
				</el-table>
			</div>

			<!-- 第三步 -->
			<div v-show="currentStep === 2">
				<div class="complete-message">
					<div v-if="Object.keys(errorItemList).length == 0">
						<span>{{ $t("serviceMaintenance.batchMaintenanceComplete") }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- 底部按钮 -->
		<span slot="footer" class="dialog-footer">
			<el-button v-if="currentStep > 0 && currentStep <= 2" @click="prevStep">
				{{ $t("serviceMaintenance.previous") }}
			</el-button>
			<el-button v-if="currentStep === 0" type="primary" :loading="submitting" @click="handleNextStep" :disabled="isUserStepDisabled">
				{{ $t("serviceMaintenance.nextStep") }}
			</el-button>
			<el-button v-if="currentStep === 1" type="primary" :loading="submitting" @click="handleNextStep" :disabled="isServiceProvidersStepDisabled">
				{{ $t("serviceMaintenance.nextStep") }}
			</el-button>

			<el-button v-if="currentStep === 2" type="primary" @click="handleClose">
				{{ $t("serviceMaintenance.complete") }}
			</el-button>
		</span>
	</el-dialog>
</template>

<script>
import { postbatchUpdateServiceProviders, getServiceProviderMaintenanceList } from "@/services/api-v2/api-v2";

export default {
	name: "ServiceMaintenanceBatchOperationDialog",
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		customerList: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			serviceMaintenanceData: [],
			loading: false,
			currentPage: 1,
			pageSize: 20,
			totalRecords: 0,

			dialogVisible: this.visible,
			currentStep: 0,
			submitting: false,
			selectedUsers: [],
			selectedServiceProviders: [],
			errorItemList: [],
		};
	},
	watch: {
		visible(val) {
			this.dialogVisible = val;
			if (val) {
				// 重置状态
				this.resetDialogState();
			}
		},
		dialogVisible(val) {
			this.$emit("update:visible", val);
		},
	},
	created() {
		this.loadServiceProviderMaintenanceList();
	},
	methods: {
		// 合并selection列(0)、客户名称列(1)和电话号码列(2)
		customerSpanMethod({ row, column, rowIndex, columnIndex }) {
			if (columnIndex === 0 || columnIndex === 1 || columnIndex === 2) {
				const currentName = row.original_phone_group?.name;

				// 检查是否与上一行相同，如果相同则当前行被合并（不显示）
				if (rowIndex > 0 && this.customerList[rowIndex - 1].original_phone_group?.name === currentName) {
					return { rowspan: 0, colspan: 0 };
				}

				// 计算向下合并的行数
				let rowspan = 1;
				for (let i = rowIndex + 1; i < this.customerList.length; i++) {
					if (this.customerList[i].original_phone_group?.name === currentName) {
						rowspan++;
					} else {
						break;
					}
				}

				return {
					rowspan: rowspan,
					colspan: 1,
				};
			}
		},

		async loadServiceProviderMaintenanceList() {
			this.loading = true;
			try {
				const { data } = await getServiceProviderMaintenanceList();

				this.serviceMaintenanceData = data;
			} catch (error) {
				this.$message.error(error);
			} finally {
				this.loading = false;
			}
		},

		// 选择客户
		handleSelectionChange(selection) {
			this.selectedUsers = selection;
		},

		// 选择服务商
		handleSelectionServiceChange(selection) {
			this.selectedServiceProviders = selection;
		},

		// 下一步
		async handleNextStep() {
			if (this.currentStep === 0) {
				this.currentStep++;
				return;
			}

			if (this.currentStep === 1) {
				// 调用上传接口
				await this.handleSubmit();
				return;
			}
		},
		// 上一步
		prevStep() {
			this.currentStep--;
		},
		// 提交
		async handleSubmit() {
			try {
				this.submitting = true;
				const phoneNumbers = [
					...new Set(
						this.selectedUsers.map((item) => {
							return item.phone_number.toString();
						})
					),
				];

				const merchantIds = this.selectedServiceProviders.map((c) => c.id);

				const requestData = {
					phone_numbers: phoneNumbers,
					merchant_ids: merchantIds,
				};

				const { data } = await postbatchUpdateServiceProviders(requestData);

				if (data.length > 0) {
					this.errorItemList = data;
				}
				// 上传成功进入第三步
				this.currentStep = 2;
			} catch (error) {
				this.$message.error(error);
			} finally {
				this.submitting = false;
			}
		},
		// 关闭弹窗
		handleClose() {
			// 重置所有状态
			this.resetDialogState();
			this.dialogVisible = false;
			this.$emit("close");
		},
		// 重置状态的逻辑优化
		resetDialogState() {
			// 立即重置数据状态
			this.currentStep = 0;
			this.selectedUsers = [];
			this.selectedServiceProviders = [];
			this.errorItemList = [];
			this.submitting = false;

			// 清除表格选择，更新DOM
			this.$nextTick(() => {
				if (this.$refs.customerTable) {
					this.$refs.customerTable.clearSelection();
				}
				if (this.$refs.serviceMaintenanceDataTable) {
					this.$refs.serviceMaintenanceDataTable.clearSelection();
				}
			});
		},
	},
	computed: {
		isUserStepDisabled() {
			return this.selectedUsers.length === 0;
		},
		isServiceProvidersStepDisabled() {
			return this.selectedServiceProviders.length === 0;
		},
		// 当在第三步时，将步骤条的 active 设置为 3，使所有步骤都显示为完成
		stepsActiveIndex() {
			return this.currentStep === 2 ? 3 : this.currentStep;
		},
	},
};
</script>

<style scoped>
.step-content {
	margin: 30px 0;
	min-height: 300px;
	position: relative;
}

.step-content > div {
	transition: opacity 0.2s ease-in-out;
}

.step-content > div[style*="display: none"] {
	position: absolute;
	visibility: hidden;
	pointer-events: none;
}

.step-content h3 {
	margin-bottom: 20px;
	color: #333;
	font-size: 18px;
}

.complete-message {
	height: 350px;
	overflow: auto;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}
</style>
